import { CalloutBlock } from "@bezoned/shared/components/callout-block.jsx";
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { Body2 } from "@fluentui/react-components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { BezonedConfirmDialog } from "../../../../components/ui/common/bezoned-confirm-dialog.js";
import { deleteTeam } from "../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../utils/init-sentry.js";
import type { Team, ToastDispatcher } from "../../tabs/members/types.js";

interface TeamEditArchiveTabProps {
  team: Team | null;
  onTeamUpdated: () => void;
  dispatchFailureToast: ToastDispatcher;
  dispatchSuccessToast: ToastDispatcher;
}

export const TeamEditArchiveTab = ({
  team,
  onTeamUpdated,
  dispatchFailureToast,
  dispatchSuccessToast,
}: TeamEditArchiveTabProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const archiveMutation = useMutation({
    mutationFn: (msTeamId: string) => deleteTeam({ msTeamId }),
    onError: (error) => {
      recordErrorInSentry(error);
      dispatchFailureToast({
        message: "Failed to archive the office. Please try again.",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamsGet"] });
      dispatchSuccessToast({ message: "Office archived successfully." });
      onTeamUpdated();
    },
  });
  const handleClick54 = useStableCallback(() => setConfirmOpen(true));

  const handleConfirm70 = useStableCallback(() => {
    if (!team) {
      return;
    }

    archiveMutation.mutate(team.id);
  });

  const handleCancel69 = useStableCallback(() => setConfirmOpen(false));

  return (
    <div className="flex min-h-52 w-full flex-col justify-between gap-4">
      <CalloutBlock variant="error">
        <Body2 className="text-red-900">
          Archiving this office removes it from the list and also archives the
          connected Team in Microsoft Teams.
        </Body2>
      </CalloutBlock>
      <div className="flex justify-end">
        <button
          aria-label="Archive this office"
          className="disabled:text-bzd-gray-400 text-sm font-medium text-red-600 hover:font-semibold hover:text-red-700 disabled:cursor-not-allowed disabled:no-underline"
          disabled={!team || archiveMutation.isPending}
          onClick={handleClick54}
          type="button"
        >
          <Body2>Archive</Body2>
        </button>
      </div>

      <BezonedConfirmDialog
        content={
          <span>
            Are you sure you want to archive{" "}
            <strong>{team?.name ?? "this office"}</strong>?
          </span>
        }
        isConfirming={archiveMutation.isPending}
        onCancel={handleCancel69}
        onConfirm={handleConfirm70}
        open={confirmOpen}
        title="Archive office"
      />
    </div>
  );
};
